/**
 * Корневой компонент приложения
 *
 * Архитектура провайдеров:
 * App → ErrorBoundary → QueryProvider → Suspense → RouterProvider(appRouter) → guards → pages
 *
 * Единый appRouter содержит все страницы (публичные и приватные).
 * Защита маршрутов — через guards (ProtectedRoute / PublicOnlyRoute / RecoveryRoute),
 * которые подписаны на useCurrentUser() и сами делают <Navigate /> при смене
 * состояния авторизации. URL в браузере всегда синхронизирован с состоянием.
 *
 * Интернационализация (i18next):
 * - Конфигурация и инициализация в @/app/config/i18n/i18n
 * - Переводы грузятся через HTTP backend из public/locales/{lang}/{namespace}.json
 * - Компоненты оборачиваются в <Suspense> чтобы автоматически ждать загрузку
 *   переводов через механизм React Suspense
 * - Пока i18next инициализируется — показывается SplashScreen (он поверх Suspense)
 *
 * Splash screen:
 * - Показывается ТОЛЬКО в нативных режимах: PWA (установлена на домашний экран),
 *   WebView-обёртки (Rork AI, Capacitor, Tauri и т.д.), TWA.
 * - В обычном браузере (Safari/Chrome на сайте) — не показывается. В браузере
 *   юзер и так видит прогресс-бар загрузки, лишний splash только раздражает.
 */

// Инициализация i18next (side-effect) — должна быть ПЕРВЫМ импортом
import '@/app/config/i18n/i18n';

import { useState, useCallback, useEffect, Suspense } from 'react';
import { RouterProvider } from 'react-router';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { QueryProvider } from '@/app/providers';
import { ErrorBoundary } from '@/app/error-boundary';
import { SplashScreen, LoadingState } from '@/components';
import { Toaster } from 'sonner';
import { appRouter } from '@/app/routing';
import { useCurrentUser } from '@/features/user';

/**
 * Определяет запущено ли приложение в "нативном" режиме — PWA, WebView-обёртка,
 * TWA или подобное. В браузере возвращает false.
 *
 * Покрытие:
 * - display-mode: standalone — стандартное PWA (Android + современные iOS) +
 *   большинство WebView-обёрток (Capacitor, Tauri, Rork AI)
 * - navigator.standalone — legacy iOS (Safari < 16.4 для PWA)
 * - display-mode: fullscreen / minimal-ui — другие режимы которые ОС считает "нативными"
 */
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;

  const matchStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches;

  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  return matchStandalone || iosStandalone;
}

/**
 * Отдельный маленький компонент — нужен только чтобы вызвать onReady
 * когда useCurrentUser закончил initial session check. Живёт внутри
 * QueryProvider, поэтому имеет доступ к react-query.
 */
function AuthBootstrap({ onReady }: { onReady: () => void }): null {
  const { isLoading } = useCurrentUser();
  useEffect(() => {
    if (!isLoading) onReady();
  }, [isLoading, onReady]);
  return null;
}

export default function App(): JSX.Element {
  // Вычисляем один раз при монтировании — режим не меняется в ходе работы
  const [shouldShowSplash] = useState(() => isStandaloneMode());
  const [showSplash, setShowSplash] = useState(shouldShowSplash);
  const [isAppReady, setIsAppReady] = useState(false);

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  const handleAppReady = useCallback(() => {
    setIsAppReady(true);
  }, []);

  return (
    <ErrorBoundary>
      <QueryProvider>
        <AuthBootstrap onReady={handleAppReady} />
        {/*
          Suspense покрывает два сценария:
          1. Загрузка i18next-переводов (useTranslation внутри компонентов).
          2. Загрузка lazy-чанков страниц при навигации (см. appRouter.tsx —
             все страницы кроме /welcome и /feed lazy-загружаются).

          LoadingState (спиннер) вместо null потому что:
          - При навигации между уже загруженным /feed → лениво-загружаемые
            settings, fallback виден юзеру явно. null давал бы мигающий
            белый экран.
          - На первом старте PWA/WebView — SplashScreen рендерится поверх
            всего пока isAppReady=false, так что LoadingState тут не мешает.
          - В обычном браузере тоже ок — первый рендер быстрее Splash'а
            (pages eager-кешированы), спиннер появится только если чанк
            реально качается.
        */}
        <Suspense fallback={<LoadingState className="flex items-center justify-center min-h-screen" />}>
          <RouterProvider router={appRouter} />
        </Suspense>
        {/*
          richColors — toast'ы типов error/success/warning получают цвет
          вместо дефолтного серого (улучшает заметность критичных сообщений).
          closeButton — крестик чтобы закрыть вручную, не ждать 4 секунды.
        */}
        <Toaster position="top-center" richColors closeButton />
      </QueryProvider>

      {showSplash && (
        <SplashScreen onComplete={handleSplashComplete} isAppReady={isAppReady} />
      )}

      <SpeedInsights />
    </ErrorBoundary>
  );
}